import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { registerVendor, registerClient, verifyVendor, verifyClient } from './api';
import './Portal.css';

type UserType = 'vendor' | 'client' | null;
type AuthMode = 'login' | 'register';

function Portal() {
  const navigate = useNavigate();
  const [userType, setUserType] = useState<UserType>(null);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form fields
  const [email, setEmail] = useState('');
  const [registrationCode, setRegistrationCode] = useState('');
  const [name, setName] = useState('');
  const [businessName, setBusinessName] = useState('');

  const VALID_REGISTRATION_CODE = process.env.REACT_APP_REGISTRATION_CODE || 'WED';

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // Validate registration code
    if (registrationCode.toUpperCase() !== VALID_REGISTRATION_CODE) {
      setError('Invalid registration code. Please contact your vendor for the correct code.');
      setIsLoading(false);
      return;
    }

    try {
      if (userType === 'vendor') {
        const result = await registerVendor(businessName, email, name);
        if (result.success) {
          setSuccess(`Vendor account created! Your Vendor ID is: ${result.vendorId}`);
          // Store in localStorage for now
          localStorage.setItem('prelude_user_type', 'vendor');
          localStorage.setItem('prelude_vendor_id', result.vendorId || '');
          localStorage.setItem('prelude_vendor_name', businessName);
          localStorage.setItem('prelude_user_email', email);
          setTimeout(() => navigate('/vendor'), 2000);
        } else {
          setError('Failed to create vendor account. Please try again.');
        }
      } else if (userType === 'client') {
        const result = await registerClient(name, email);
        if (result.success) {
          setSuccess(`Client account created! Your Client ID is: ${result.clientId}`);
          localStorage.setItem('prelude_user_type', 'client');
          localStorage.setItem('prelude_client_id', result.clientId || '');
          localStorage.setItem('prelude_client_name', name);
          localStorage.setItem('prelude_user_email', email);
          setTimeout(() => navigate('/player'), 2000);
        } else {
          setError('Failed to create client account. Please try again.');
        }
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (userType === 'vendor') {
        // Try to verify vendor email exists in database
        try {
          const result = await verifyVendor(email);
          if (result.exists) {
            // Store verified vendor info
            localStorage.setItem('prelude_vendor_id', result.vendorId || '');
            localStorage.setItem('prelude_vendor_name', result.vendorName || businessName);
            localStorage.setItem('prelude_contact_name', result.contactName || '');
          } else if (result.exists === false) {
            setError('Email not found. Please register first or check your email address.');
            setIsLoading(false);
            return;
          }
        } catch (verifyError) {
          // If verification fails (backend not updated), allow login with provided info
          console.warn('Vendor verification not available, using provided info');
          localStorage.setItem('prelude_vendor_name', businessName);
        }

        localStorage.setItem('prelude_user_type', 'vendor');
        localStorage.setItem('prelude_user_email', email);

        setIsLoading(false);
        navigate('/vendor');
      } else if (userType === 'client') {
        // Try to verify client email exists in database
        try {
          const result = await verifyClient(email);
          if (result.exists) {
            // Store verified client info
            localStorage.setItem('prelude_client_id', result.clientId || '');
            localStorage.setItem('prelude_client_name', result.clientName || '');
            localStorage.setItem('prelude_vendor_id', result.vendorId || '');
          } else if (result.exists === false) {
            setError('Email not found. Please register first or check your email address.');
            setIsLoading(false);
            return;
          }
        } catch (verifyError) {
          // If verification fails (backend not updated), allow login
          console.warn('Client verification not available, proceeding without verification');
        }

        localStorage.setItem('prelude_user_type', 'client');
        localStorage.setItem('prelude_user_email', email);

        setIsLoading(false);
        navigate('/player');
      }
    } catch (err) {
      setError('An error occurred while verifying your account. Please try again.');
      console.error(err);
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setEmail('');
    setRegistrationCode('');
    setName('');
    setBusinessName('');
    setError(null);
    setSuccess(null);
  };

  return (
    <div className="portal-page">
      <header className="portal-header">
        <Link to="/" className="back-link">← Back to Home</Link>
        <div className="logo">Prelude</div>
      </header>

      <div className="portal-content">
        {!userType ? (
          <div className="user-type-selection">
            <h1>Welcome to Prelude Portal</h1>
            <p>Choose your account type to continue</p>
            <div className="type-cards">
              <button
                className="type-card"
                onClick={() => setUserType('vendor')}
              >
                <div className="type-icon">🎻</div>
                <h3>I'm a Vendor</h3>
                <p>String quartet, musician, or wedding music provider</p>
              </button>
              <button
                className="type-card"
                onClick={() => setUserType('client')}
              >
                <div className="type-icon">💒</div>
                <h3>I'm a Client</h3>
                <p>Couple planning a wedding or event</p>
              </button>
            </div>
          </div>
        ) : (
          <div className="auth-form-container">
            <button
              className="change-type"
              onClick={() => {
                setUserType(null);
                resetForm();
              }}
            >
              ← Change account type
            </button>

            <h2>
              {authMode === 'login' ? 'Sign In' : 'Create Account'} as {userType === 'vendor' ? 'Vendor' : 'Client'}
            </h2>

            <div className="auth-tabs">
              <button
                className={`auth-tab ${authMode === 'login' ? 'active' : ''}`}
                onClick={() => {
                  setAuthMode('login');
                  resetForm();
                }}
              >
                Sign In
              </button>
              <button
                className={`auth-tab ${authMode === 'register' ? 'active' : ''}`}
                onClick={() => {
                  setAuthMode('register');
                  resetForm();
                }}
              >
                Register
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <form onSubmit={authMode === 'login' ? handleLogin : handleRegister}>
              {userType === 'vendor' && (
                <div className="form-group">
                  <label>Business Name</label>
                  <input
                    type="text"
                    value={businessName}
                    onChange={(e) => setBusinessName(e.target.value)}
                    placeholder="Harmony String Quartet"
                    required
                  />
                </div>
              )}

              {authMode === 'register' && (
                <>
                  <div className="form-group">
                    <label>{userType === 'vendor' ? 'Contact Name' : 'Your Name'}</label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              {authMode === 'register' && (
                <div className="form-group">
                  <label>Registration Code</label>
                  <input
                    type="text"
                    value={registrationCode}
                    onChange={(e) => setRegistrationCode(e.target.value)}
                    placeholder="Enter your registration code"
                    required
                  />
                </div>
              )}

              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : (authMode === 'login' ? 'Sign In' : 'Create Account')}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default Portal;
